import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { serviceReportsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { S } from '../staff/StaffUI';
import ReportPaper from './ReportPaper';
import {
  STAT_KEYS, REPORT_STATUS, reportStatusKey, monthNo, statVal, asArray, fmtMD, fmtMDHM, toDate,
  isSupervisorUser, personName,
} from './sopShared';

// 老师起草月度服务报告 — prototype ReportDraft. Opened from the CRM status line
// (「10 月报告 · 待起草」) and from /service-reports. Stats are computed by the
// server and read-only; the coach writes the opening, ticks which timeline
// entries the member sees, writes 新了解 / 下月计划, and submits for review.
// The draft autosaves. Read-only once submitted or published.
//
// Props: reportId, isZh, onClose, onChanged, onOpenRecords(tabKey) (optional —
// from inside the CRM the stat links jump to that tab), memberName.

const keyOf = (x) => (x && x.source && x.source_id != null ? `${x.source}:${x.source_id}` : `${x && x.date}|${x && x.text}`);
const isInternal = (x) => !!(x && (x.internal || x.source === 'complaint'));

const STAT_LINK = {
  contacts_two_way: ['tasks', (n, isZh) => (isZh ? `${n} 条双向沟通记录` : `${n} two-way contacts`)],
  recommended: ['matches', (n, isZh) => (isZh ? '推荐记录' : 'Recommendations')],
  interested: ['matches', (n, isZh) => (isZh ? '推荐记录' : 'Recommendations')],
  meetings: ['meets', (n, isZh) => (isZh ? '约见记录' : 'Meetings')],
};

export default function ServiceReportEditor({ reportId, isZh = true, onClose, onChanged, onOpenRecords, memberName = '' }) {
  const t = (zh, en) => (isZh ? zh : en);
  const navigate = useNavigate();
  const { user } = useAuth();
  const supervisor = isSupervisorUser(user);
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState({ opening: '', timeline: [], learned: '', plan: '', internal_note: '' });
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [vw, setVw] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  const dirty = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await serviceReportsAPI.get(reportId);
      const r = (res.data && (res.data.report || res.data)) || {};
      const base = asArray(r.timeline).map((x) => ({ ...x, include: x.include !== false }));
      const seen = new Set(base.map(keyOf));
      asArray(r.timeline_candidates).forEach((c) => {
        const k = keyOf(c);
        if (seen.has(k)) return;
        seen.add(k);
        base.push({ ...c, include: c.include != null ? !!c.include : !isInternal(c) });
      });
      base.sort((a, b) => (toDate(a.date)?.getTime() || 0) - (toDate(b.date)?.getTime() || 0));
      setReport(r);
      setForm({
        opening: r.opening || '', timeline: base, learned: r.learned || '', plan: r.plan || '', internal_note: r.internal_note || '',
      });
      setSavedAt(toDate(r.updated_at));
      dirty.current = false;
    } catch (e) {
      setFailed(true);
    }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const status = report ? (report.status || 'draft') : 'draft';
  const editable = !!report && ['draft', 'returned'].includes(status) && report.can_edit !== false;
  const stKey = reportStatusKey(report);
  const st = REPORT_STATUS[stKey] || REPORT_STATUS.draft;

  const save = useCallback(async (quiet = true) => {
    if (!report) return false;
    setSaving(true);
    try {
      const f = formRef.current;
      await serviceReportsAPI.save(reportId, {
        opening: f.opening, timeline: f.timeline, learned: f.learned, plan: f.plan, internal_note: f.internal_note,
      });
      dirty.current = false;
      setSavedAt(new Date());
      if (!quiet) toast.success(t('草稿已保存', 'Draft saved'));
      return true;
    } catch (e) {
      if (!quiet) toast.error(e?.response?.data?.error || t('保存失败', 'Could not save'));
      return false;
    } finally { setSaving(false); }
  }, [report, reportId, isZh]);

  // Autosave 1.5 s after the last edit.
  useEffect(() => {
    if (!editable || !dirty.current) return undefined;
    const h = setTimeout(() => { save(true); }, 1500);
    return () => clearTimeout(h);
  }, [form, editable, save]);

  const edit = (patch) => { dirty.current = true; setForm((f) => ({ ...f, ...patch })); };
  const editItem = (i, patch) => {
    dirty.current = true;
    setForm((f) => ({ ...f, timeline: f.timeline.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  };

  const aiDraft = async () => {
    if (aiBusy) return;
    const hasText = form.opening.trim() || form.learned.trim() || form.plan.trim();
    // eslint-disable-next-line no-alert
    if (hasText && !window.confirm(t('AI 起草会替换开场白、新了解和下月计划，继续吗？', 'The AI draft replaces the opening, learnings and plan. Continue?'))) return;
    setAiBusy(true);
    try {
      const res = await serviceReportsAPI.aiDraft(reportId);
      const d = res.data || {};
      edit({
        opening: d.opening != null ? d.opening : form.opening,
        learned: d.learned != null ? d.learned : form.learned,
        plan: d.plan != null ? d.plan : form.plan,
      });
      toast.success(t('已起草，请逐句核对后再提交', 'Drafted — check every line before submitting'));
    } catch (e) {
      toast.error(e?.response?.data?.error || t('AI 起草失败', 'AI draft failed'));
    } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!form.opening.trim() || !form.plan.trim()) {
      toast.error(t('请先写开场白和下月计划', 'Write the opening and the plan first'));
      return;
    }
    setSubmitting(true);
    try {
      const ok = await save(true);
      if (!ok) throw new Error('save');
      await serviceReportsAPI.submit(reportId);
      toast.success(t('已提交主管审核', 'Submitted for review'));
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('提交失败', 'Could not submit'));
    } finally { setSubmitting(false); }
  };

  const name = memberName
    || personName(report && report.profile)
    || (report && (report.profile_name || personName(report)))
    || '';
  const offline = report && ((report.profile && report.profile.offline_id) || report.offline_id);
  const k = Number(report && report.period_index) || 1;
  const mon = monthNo(report);
  const stats = (report && report.stats) || {};

  const preview = useMemo(() => ({
    period_index: report && report.period_index,
    period_start: report && report.period_start,
    period_end: report && report.period_end,
    opening: form.opening,
    stats: Object.fromEntries(STAT_KEYS.map(([sk]) => [sk, statVal(stats[sk])])),
    timeline: form.timeline.filter((x) => x.include).map((x) => ({ date: x.date, text: x.text })),
    learned: form.learned,
    plan: form.plan,
  }), [report, form, stats]);

  const stepIdx = status === 'published' ? 2 : status === 'submitted' ? 1 : 0;
  const narrow = vw < 980;
  const ta = { font: 'inherit', fontSize: 14, border: `1px solid #E4DFD7`, borderRadius: 2, padding: '8px 10px', resize: 'vertical', width: '100%', boxSizing: 'border-box', background: editable ? '#FFFDF9' : S.soft, color: S.ink };
  const h3 = { fontSize: 14, fontWeight: 700, color: S.ink };

  const body = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,16,.5)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-tour="report-editor" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('月度服务报告', 'Monthly service report')}
        style={{ background: '#FFFDF9', color: S.ink, borderRadius: 2, width: 'min(1280px, 100%)', maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'none', fontFamily: 'inherit' }}>
        <header style={{ padding: '16px 22px', borderBottom: `1px solid ${S.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 23, fontWeight: 500 }}>
              {mon ? t(`${mon} 月服务报告`, `Month ${mon} service report`) : t('月度服务报告', 'Monthly service report')}
              {(offline || name) ? ` · ${[offline, name].filter(Boolean).join(' ')}` : ''}
              {report && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 2, background: st.bg, color: st.color, verticalAlign: 'middle' }}>{isZh ? st.zh : st.en}</span>}
            </h2>
            {report && (
              <div style={{ fontSize: 13, color: S.sub, marginTop: 2 }}>
                {t(`第 ${k} 期 · 服务第 ${30 * (k - 1) + 1}–${30 * k} 天`, `Period ${k} · days ${30 * (k - 1) + 1}–${30 * k}`)}
                {report.due_at ? t(` · ${fmtMD(report.due_at)} 前提交`, ` · due ${fmtMD(report.due_at)}`) : ''}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <ol aria-label={t('进度', 'Progress')} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              {[t('老师起草', 'Coach drafts'), t('主管审核', 'Supervisor review'), t('发布给会员', 'Published')].map((lbl, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <li aria-hidden="true" style={{ width: 28, height: 1, background: '#E4DFD7' }} />}
                  <li style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: i <= stepIdx ? S.brand : S.mute, fontWeight: i === stepIdx ? 600 : 400 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, background: i <= stepIdx ? '#111111' : 'transparent', color: i <= stepIdx ? '#FFFDF9' : S.mute, border: i <= stepIdx ? 'none' : '1px solid #E4DFD7' }}>{i + 1}</span>
                    {lbl}
                  </li>
                </React.Fragment>
              ))}
            </ol>
            <button type="button" onClick={onClose} aria-label={t('关闭', 'Close')} style={{ width: 34, height: 34, border: 'none', background: 'none', color: S.sub, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
          </div>
        </header>

        {failed ? (
          <div style={{ padding: 40, textAlign: 'center', color: S.sub, fontSize: 14 }}>
            {t('报告暂时打不开。服务报告功能可能尚未上线，或你没有这位会员的权限。', 'The report could not be opened — the feature may not be live yet, or you do not serve this member.')}
          </div>
        ) : !report ? (
          <div style={{ padding: 40, textAlign: 'center', color: S.mute, fontSize: 14 }}>{t('加载中…', 'Loading…')}</div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'grid', gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'minmax(0, 1fr) 360px' }}>
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16, borderRight: narrow ? 'none' : `1px solid ${S.line}`, minWidth: 0 }}>
              {status === 'returned' && report.review_note && (
                <div role="alert" style={{ fontSize: 13, background: '#EEE8EF', color: '#6B4E71', borderRadius: 2, padding: '9px 12px' }}>
                  <b style={{ fontWeight: 600 }}>{t('主管退回意见：', 'Returned: ')}</b>{report.review_note}
                </div>
              )}
              <section aria-label={t('本月数字', 'Numbers')} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <div style={h3}>{t('本月数字', 'This period in numbers')}</div>
                  <span style={{ fontSize: 12, color: S.mute }}>{t('系统根据记录自动统计，不能手改', 'Counted from the records — not editable')}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${vw < 560 ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
                  {STAT_KEYS.map(([sk, label]) => {
                    const n = statVal(stats[sk]);
                    const [tab, linkText] = STAT_LINK[sk];
                    return (
                      <div key={sk} style={{ background: S.soft, borderRadius: 2, padding: '10px 12px' }}>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>{n}</div>
                        <div style={{ fontSize: 12.5, color: S.sub }}>{isZh ? label : STAT_KEYS.find((x) => x[0] === sk)[4]}</div>
                        {onOpenRecords ? (
                          <button type="button" onClick={() => { onOpenRecords(tab); onClose && onClose(); }}
                            style={{ fontSize: 11.5, border: 'none', background: 'none', padding: 0, color: '#111111', cursor: 'pointer', fontFamily: 'inherit' }}>
                            {linkText(n, isZh)}
                          </button>
                        ) : <span style={{ fontSize: 11.5, color: S.mute }}>{linkText(n, isZh)}</span>}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section aria-label={t('开场白', 'Opening')} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="sr-open" style={h3}>{t('开场白', 'Opening')}</label>
                  {editable && (
                    <button type="button" onClick={aiDraft} disabled={aiBusy}
                      style={{ height: 28, padding: '0 10px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', font: 'inherit', fontSize: 12.5, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: aiBusy ? 'wait' : 'pointer', color: S.ink }}>
                      <Sparkles size={14} />{aiBusy ? t('起草中…', 'Drafting…') : t('AI 起草全文', 'AI draft')}
                    </button>
                  )}
                </div>
                <textarea id="sr-open" rows={2} value={form.opening} readOnly={!editable} onChange={(e) => edit({ opening: e.target.value })} style={ta} />
              </section>

              <section aria-label={t('本月进程', 'Timeline')} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <div style={h3}>{t('本月进程', 'Timeline')}</div>
                  <span style={{ fontSize: 12, color: S.mute }}>{t('从记录里自动列出，勾选要给会员看的', 'Listed from the records — tick what the member sees')}</span>
                </div>
                {form.timeline.length === 0 ? (
                  <div style={{ fontSize: 13, color: S.mute, padding: '8px 0' }}>{t('这一期还没有可列出的记录。', 'Nothing recorded for this period yet.')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', fontSize: 13.5 }}>
                    {form.timeline.map((x, i) => {
                      const internal = isInternal(x);
                      return (
                        <div key={keyOf(x) + i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid #EFEAE2', color: x.include ? S.ink : S.mute }}>
                          <input type="checkbox" checked={!!x.include} disabled={!editable} aria-label={t('给会员看', 'Show to member')}
                            onChange={(e) => editItem(i, { include: e.target.checked })} style={{ accentColor: '#111111', flexShrink: 0 }} />
                          <span style={{ width: 44, color: S.sub, flexShrink: 0 }}>{fmtMD(x.date)}</span>
                          {editable ? (
                            <input value={x.text || ''} onChange={(e) => editItem(i, { text: e.target.value })}
                              aria-label={t('进程内容', 'Entry text')}
                              style={{ flex: 1, minWidth: 0, font: 'inherit', fontSize: 13.5, border: '1px solid transparent', borderRadius: 2, padding: '3px 6px', background: 'transparent', color: 'inherit' }}
                              onFocus={(e) => { e.target.style.borderColor = '#E4DFD7'; }}
                              onBlur={(e) => { e.target.style.borderColor = 'transparent'; }} />
                          ) : <span style={{ flex: 1, minWidth: 0 }}>{x.text}</span>}
                          {internal && <span style={{ fontSize: 11.5, color: S.mute, flexShrink: 0 }}>{t('内部，默认不显示', 'internal')}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <div style={{ display: 'grid', gridTemplateColumns: vw < 640 ? 'minmax(0,1fr)' : 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
                <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label htmlFor="sr-learn" style={h3}>{t('我们对您的新了解', 'What we learned')}</label>
                  <textarea id="sr-learn" rows={4} value={form.learned} readOnly={!editable} onChange={(e) => edit({ learned: e.target.value })} style={{ ...ta, fontSize: 13.5 }} />
                </section>
                <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label htmlFor="sr-plan" style={h3}>{t('下月计划', 'Next month')}</label>
                  <textarea id="sr-plan" rows={4} value={form.plan} readOnly={!editable} onChange={(e) => edit({ plan: e.target.value })}
                    placeholder={t('每行一项', 'One item per line')} style={{ ...ta, fontSize: 13.5 }} />
                </section>
              </div>
              <div style={{ fontSize: 12.5, color: '#7A4A15', background: '#F3EADB', borderRadius: 2, padding: '8px 12px' }}>
                {t('不要写其他会员的姓名、照片或内部评价；没有记录的事情不要写进报告。', 'No other member’s name, photo or internal assessment; nothing that is not in the records.')}
              </div>
            </div>

            <aside aria-label={t('会员看到的样子', 'What the member sees')} style={{ background: '#F6F3EE', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: S.sub }}>{t('会员看到的样子', 'What the member sees')}</div>
              <ReportPaper report={preview} isZh={isZh} compact />
              <label htmlFor="sr-internal" style={{ fontSize: 12.5, color: S.sub, marginTop: 6 }}>{t('给主管的话（会员看不到）', 'Note to the supervisor (hidden from the member)')}</label>
              <textarea id="sr-internal" rows={3} value={form.internal_note} readOnly={!editable} onChange={(e) => edit({ internal_note: e.target.value })}
                style={{ ...ta, fontSize: 13 }} />
            </aside>
          </div>
        )}

        {report && !failed && (
          <footer style={{ padding: '12px 22px', borderTop: `1px solid ${S.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: S.sub }}>
              {editable
                ? (saving ? t('保存中…', 'Saving…') : savedAt ? t(`草稿已自动保存 · ${fmtMDHM(savedAt)}`, `Draft saved · ${fmtMDHM(savedAt)}`) : t('修改会自动保存', 'Changes save automatically'))
                : status === 'submitted' ? t(`已提交${report.submitted_at ? ` · ${fmtMDHM(report.submitted_at)}` : ''}，等待主管审核`, 'Submitted — waiting for review')
                  : status === 'published' ? t(`已发布给会员${report.published_at ? ` · ${fmtMDHM(report.published_at)}` : ''}`, 'Published to the member')
                    : t('只读', 'Read-only')}
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              {editable ? (
                <>
                  <button type="button" onClick={() => save(false)} disabled={saving || submitting}
                    style={{ height: 38, padding: '0 16px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', font: 'inherit', fontSize: 14, cursor: 'pointer', color: S.ink }}>
                    {t('保存草稿', 'Save draft')}
                  </button>
                  <button type="button" onClick={submit} disabled={submitting}
                    style={{ height: 38, padding: '0 20px', borderRadius: 2, border: 'none', background: '#111111', color: '#FFFDF9', font: 'inherit', fontSize: 14, fontWeight: 500, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? t('提交中…', 'Submitting…') : t('提交主管审核', 'Submit for review')}
                  </button>
                </>
              ) : (supervisor && status === 'submitted') ? (
                <button type="button" onClick={() => { onClose && onClose(); navigate(`/service-reports?id=${reportId}`); }}
                  style={{ height: 38, padding: '0 20px', borderRadius: 2, border: 'none', background: '#111111', color: '#FFFDF9', font: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  {t('去审核 →', 'Review →')}
                </button>
              ) : (
                <button type="button" onClick={onClose}
                  style={{ height: 38, padding: '0 16px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', font: 'inherit', fontSize: 14, cursor: 'pointer', color: S.ink }}>
                  {t('关闭', 'Close')}
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
}
